import { memo } from "react"
import { Database, Play, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import Loader from "@/components/ui/loader"
import { parseApiError } from "@/lib/http/client"
import { ChartTab } from "./chart-tab"
import { NumberTab } from "./number-tab"
import { TableTab } from "./table-tab"

export const QueryResultsPanel = memo(function QueryResultsPanel(props: {
  activeTab: "table" | "chart" | "number"
  setActiveTab: (value: "table" | "chart" | "number") => void

  selectedResource: string
  onSelectResource: (value: string) => void
  onAddResource: () => void
  resources: { id: string; name: string }[]

  activeQueryId: string
  activeSql: string
  activeResult: Record<string, unknown>[] | undefined
  containerFields: any[]

  isRunning: boolean
  runError: boolean
  runErrorValue: unknown
  isRunPending: boolean
  onRunQuery: () => void

  onExport: () => void
  canExport: boolean

  chartConfig: { chartType: "bar" | "line" | "pie"; xAxis: string; yAxis: string }
  setChartConfig: (cfg: { chartType: "bar" | "line" | "pie"; xAxis: string; yAxis: string }) => void

  numberConfig: {
    column: string
    label?: string
    fontSize?: number
    color?: string
    labelColor?: string
  }
  setNumberConfig: (cfg: {
    column: string
    label?: string
    fontSize?: number
    color?: string
    labelColor?: string
  }) => void

  widgetConfigRef: React.MutableRefObject<{ chartConfig?: any; numberConfig?: any } | null>
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0F0F12]">
      <Tabs
        value={props.activeTab}
        onValueChange={(v) => props.setActiveTab(v as typeof props.activeTab)}
        className="flex h-full flex-col"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-3 py-1.5 dark:border-gray-800">
          <TabsList className="h-7 bg-transparent p-0">
            <TabsTrigger
              value="table"
              className="h-7 px-2 text-xs data-[state=active]:border-b-2 data-[state=active]:border-gray-900 dark:data-[state=active]:border-white"
            >
              Table
            </TabsTrigger>
            <TabsTrigger
              value="chart"
              className="h-7 px-2 text-xs data-[state=active]:border-b-2 data-[state=active]:border-gray-900 dark:data-[state=active]:border-white"
            >
              Chart
            </TabsTrigger>
            <TabsTrigger
              value="number"
              className="h-7 px-2 text-xs data-[state=active]:border-b-2 data-[state=active]:border-gray-900 dark:data-[state=active]:border-white"
            >
              Number
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-1.5">
            <Select
              value={props.selectedResource}
              onValueChange={(value) => {
                if (value === "__add_resource__") props.onAddResource()
                else props.onSelectResource(value)
              }}
            >
              <SelectTrigger className="h-7 w-36 text-xs">
                <SelectValue placeholder="Select Resource">
                  {props.selectedResource
                    ? props.resources.find((r) => r.id === props.selectedResource)?.name || "Select Resource"
                    : "Select Resource"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {props.resources.map((resource) => (
                  <SelectItem key={resource.id} value={resource.id} className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <Database className="h-3.5 w-3.5" />
                      <span>{resource.name}</span>
                    </div>
                  </SelectItem>
                ))}
                <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                <SelectItem value="__add_resource__" className="text-xs">
                  <div className="flex items-center gap-1.5">
                    <Plus className="h-3.5 w-3.5" />
                    <span>Add resource</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="xs"
              onClick={props.onExport}
              tooltip={props.activeSql.trim() ? "" : "Run query to enable"}
              disabled={!props.canExport}
            >
              Export to Board
            </Button>

            <Button
              onClick={props.onRunQuery}
              size="sm"
              disabled={props.isRunPending || !props.selectedResource}
              className="h-7 bg-gray-900 px-2.5 text-xs text-white hover:bg-black disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
            >
              {props.isRunPending ? (
                <span className="mr-1.5 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-current" />
              ) : (
                <Play className="mr-1.5 h-3.5 w-3.5" />
              )}
              Run
              <span className="ml-1.5 text-[10px] opacity-70">⌘⏎</span>
            </Button>
          </div>
        </div>

        <TabsContent
          value="table"
          className={`m-0 flex min-h-0 flex-1 flex-col overflow-hidden px-3 ${props.activeTab === "table" ? "" : "hidden"}`}
        >
          {props.isRunning ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <Loader className="h-7 w-7" />
            </div>
          ) : props.runError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
              {parseApiError(props.runErrorValue as any)}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <TableTab
                data={props.activeResult}
                emptyMessage={
                  Array.isArray(props.activeResult) ? "Query returned no rows." : "Click Run to execute your query."
                }
                containerFields={props.containerFields}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent
          value="chart"
          forceMount
          className="m-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
        >
          <ChartTab
            key={`chart-${props.activeQueryId}`}
            data={props.activeResult}
            onConfigChange={props.setChartConfig}
            initialConfig={
              props.widgetConfigRef.current?.chartConfig ||
              (props.chartConfig.xAxis || props.chartConfig.yAxis
                ? {
                    chartType: props.chartConfig.chartType,
                    xAxis: props.chartConfig.xAxis,
                    yAxis: props.chartConfig.yAxis,
                  }
                : undefined)
            }
          />
        </TabsContent>

        <TabsContent
          value="number"
          forceMount
          className="m-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
        >
          <NumberTab
            key={`number-${props.activeQueryId}-${props.numberConfig.column || ""}-${props.widgetConfigRef.current?.numberConfig ? "widget" : ""}`}
            data={props.activeResult}
            onConfigChange={props.setNumberConfig}
            initialConfig={
              props.widgetConfigRef.current?.numberConfig ||
              (props.numberConfig.column
                ? {
                    column: props.numberConfig.column,
                    label: props.numberConfig.label,
                    fontSize: props.numberConfig.fontSize,
                    color: props.numberConfig.color,
                    labelColor: props.numberConfig.labelColor,
                  }
                : undefined)
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  )
})
